import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {KennethMustDie} from "../KennethMustDie";
import {BonusFeatureData} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/BonusFeatures";
import {BetBoostData} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/bethandler/BetHandler";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APIBetType} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetType";
import {IKMDInitData} from "../server/IKMDData";
import {KMDSound} from "../sound/KMDSound";
import {BetFeature} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/bethandler/BetFeatureController";
import {gsap} from "gsap";

export class KMDxBet extends PIXI.Container implements StageLayer {

    private readonly _betBoostAllowed: boolean;
    private _btn: PIXI.spine.Spine;
    private _toggled: boolean;
    private _xBetPaused: boolean;
    private _enableTl: gsap.core.Timeline | null;

    constructor() {
        super();
        this.name = "KMDxBet";

        this._betBoostAllowed = KennethMustDie.apiPlugin.gameClientConfiguration.boostedBetAllowed;

        if (this._betBoostAllowed) {
            this.init();
        }
    }

    public enable(flag: boolean, delay: number): void {
        if (!this._betBoostAllowed) {
            return;
        }

        // #367
        if (this._enableTl) {
            this._enableTl.pause();
            this._enableTl.kill();

            this._enableTl = null;
        }

        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(() => {
            this.doEnableAction(flag);
        }, delay);

        this._enableTl = tl;
    }

    public turnOff(): void {
        if (!this._betBoostAllowed) {
            return;
        }

        this._toggled = false;
        this._btn.state.setAnimation(0, "off", false);

        KennethMustDie.xBetReelFrame.toggleXBetFrame(false);
    }

    public turnOn(): void {
        if (!this._betBoostAllowed) {
            return;
        }

        this._toggled = true;
        this._btn.state.setAnimation(0, "on", false);

        if (this.visible) {
            KennethMustDie.xBetReelFrame.toggleXBetFrame(true);
        }
    }

    public toggleVisibility(value: boolean): void {
        if (!this._betBoostAllowed) {
            return;
        }

        this.visible = value;
        this._xBetPaused = !value;

        const isFreeBets = KennethMustDie.apiPlugin.promotionHandler.hasFreeBets() || KennethMustDie.apiPlugin.promotionHandler.hasFreeFeatureBet();
        if (isFreeBets){
            this._xBetPaused = true;
        }

        if (!value) {
            KennethMustDie.xBetReelFrame.toggleXBetFrame(false);
        } else if (value && this._toggled) {
            KennethMustDie.xBetReelFrame.toggleXBetFrame(true);
        }
    }

    public onRestore(): void {
        if (!this._betBoostAllowed) {
            return;
        }

        const initData: IKMDInitData = KennethMustDie.model.initData;

        // if current mode is any of the bonus game mode OR it's going to be in bonus game
        if ((initData.mode.indexOf("FREESPIN") > -1) || (initData.mode.indexOf("NORMAL") > -1 && initData.nextMode.indexOf("FREESPIN") > -1)) {
            this.toggleVisibility(false);
        }
    }

    public onBet(data: APIFeatureBetEventData): void {
        this.doEnableAction(false); // #448, disable this, without timeline

        if (data.type === APIBetType.FEATURE_BET && data.featureName !== "BOOSTED_BET" && this._toggled) {
            this.turnOff();
            this._xBetPaused = true;

            KennethMustDie.apiPlugin.betHandler.setBoost(undefined);

            KennethMustDie.xBetReelFrame.toggleXBetFrame(false);
        }
    }

    private init(): void {
        this._btn = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("xBet"));
        this._btn.position.set(33, 624);
        this._btn.scale.set(0.8);
        this._btn.hitArea = new PIXI.Rectangle(-45, -104, 90, 209);
        this.addChild(this._btn);

        this._btn.on('pointertap', () => this.toggleBoost());

        this.turnOff();
        this.enable(true, 0);

        if (KennethMustDie.apiPlugin.freeBets.hasFreeBets()) {
            this.doEnableAction(false);
            this._xBetPaused = true;
        }
        KennethMustDie.apiPlugin.freeBets.events.on(KennethMustDie.apiPlugin.freeBets.FREE_BETS_START, () => {
            this.doEnableAction(false);
            this._xBetPaused = true;
        });
        KennethMustDie.apiPlugin.freeBets.events.on(KennethMustDie.apiPlugin.freeBets.FREE_BETS_END, () => {
            this._xBetPaused = false;
            this.enable(true, 0.3);
        });
        KennethMustDie.apiPlugin.events.on("extFreeRoundsEnded", () => {
            this._xBetPaused = false;
            this.enable(true, 0.3);
        });

        KennethMustDie.apiPlugin.events.on(APIEvent.SELECTED_FEATURE_BET_CHANGED, () => this.onSelectedFeatureBetChanged());
    }

    private onSelectedFeatureBetChanged(): void {
        const activeBetFeature: BetFeature | undefined = KennethMustDie.apiPlugin.betFeatureController.getActiveBetFeature();

        if (activeBetFeature?.name === "BOOSTED_BET") {
            this.turnOn();
        } else {
            this.turnOff();

            // #452
            if (!activeBetFeature?.name) {
                KennethMustDie.buyFeatureName = "";
            }
        }
    }

    private doEnableAction(flag: boolean): void {
        // #448
        if (!this._betBoostAllowed || this._xBetPaused) {
            return;
        }

        // #448
        if (this._enableTl) {
            this._enableTl.pause();
            this._enableTl.kill();

            this._enableTl = null;
        }

        this._btn.interactive = flag;
        this._btn.buttonMode = flag;
    }

    private toggleBoost(): void {
        if (!this._betBoostAllowed || this._xBetPaused) {
            return;
        }

        if (this._toggled) {
            KennethMustDie.apiPlugin.betHandler.setBoost(undefined);

            // #227
            KennethMustDie.soundPlayer.playSound(KMDSound.X_BET_OFF);
        } else {
            const featureData: BonusFeatureData | undefined = KennethMustDie.apiPlugin.bonusFeatures.getFeatureDataByName("BOOSTED_BET");
            if (featureData) {
                let data: BetBoostData = {
                    featureName: featureData.name,
                    price: featureData.price
                }
                KennethMustDie.apiPlugin.betHandler.setBoost(data);

                // #227
                KennethMustDie.soundPlayer.playSound(KMDSound.X_BET_ON);
            }

            // this will trigger the event -> APIEvent.SELECTED_FEATURE_BET_CHANGED
        }
    }
}